import { useRef, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

/**
 * Abas com o teclado como caminho de primeira classe (PLANO_GERAL §4.4).
 *
 * Existe porque as duas telas que tinham abas as fizeram à mão, e as duas pararam no
 * meio: `role="tab"` sem `aria-controls`, nenhum `role="tabpanel"` do outro lado e as
 * setas sem efeito. Quem usa leitor de tela ouvia "aba Montagem, selecionada" e não tinha
 * como saber o que ela controla; quem navega por teclado tabulava por todas as abas em
 * vez de percorrê-las com ←/→.
 *
 * Não é Radix porque não precisa ser: o padrão de abas é um `tablist`, um índice e o foco
 * andando junto com ele. O que não dava para deixar solto era **o padrão** — corrigir uma
 * tela só deixaria a outra errada, e a terceira nasceria copiando qual das duas desse na
 * telha.
 *
 * O foco anda com a seleção (*automatic activation*), que é o comportamento recomendado
 * quando trocar de aba não custa nada — e aqui não custa: as duas metades já estão em
 * memória.
 */

export interface TabItem<T extends string> {
  readonly id: T;
  readonly label: string;
}

/** `base` amarra os dois lados: a aba aponta para o painel, e o painel de volta para ela. */
function tabDomId(base: string, id: string): string {
  return `${base}-aba-${id}`;
}

function panelDomId(base: string, id: string): string {
  return `${base}-painel-${id}`;
}

export function TabList<T extends string>({
  base,
  items,
  atual,
  onEscolher,
  label,
  variant = 'pill',
  className,
}: {
  /** Prefixo dos `id` desta faixa — precisa ser o mesmo do `TabPanel` que ela controla. */
  readonly base: string;
  readonly items: readonly TabItem<T>[];
  readonly atual: T;
  readonly onEscolher: (id: T) => void;
  /** O que esta faixa divide, dito para quem não vê a tela. */
  readonly label: string;
  /** `pill` para faixa solta; `underline` para a que encosta no conteúdo. */
  readonly variant?: 'pill' | 'underline';
  readonly className?: string;
}) {
  const faixa = useRef<HTMLDivElement>(null);

  // Setas, Home e End andam pela faixa; Tab sai dela. É o gesto do padrão, e sem ele a
  // faixa vira uma fila de botões que a tabulação percorre um a um.
  const aoTeclar = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    const daqui = items.findIndex((item) => item.id === atual);
    const destino =
      event.key === 'ArrowRight'
        ? (daqui + 1) % items.length
        : event.key === 'ArrowLeft'
          ? (daqui - 1 + items.length) % items.length
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? items.length - 1
              : undefined;

    const escolhido = destino === undefined ? undefined : items[destino];
    if (escolhido === undefined) return;

    event.preventDefault();
    onEscolher(escolhido.id);
    faixa.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[destino!]?.focus();
  };

  return (
    <div
      ref={faixa}
      role="tablist"
      aria-label={label}
      onKeyDown={aoTeclar}
      className={cn(
        variant === 'underline'
          ? 'flex gap-1 border-b border-(--color-border-subtle)'
          : 'flex flex-wrap gap-1',
        className,
      )}
    >
      {items.map((item) => {
        const selecionada = item.id === atual;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            id={tabDomId(base, item.id)}
            aria-selected={selecionada}
            aria-controls={panelDomId(base, item.id)}
            // Foco rolante: só a aba da vez está na ordem de tabulação, e as setas
            // percorrem o resto.
            tabIndex={selecionada ? 0 : -1}
            onClick={() => onEscolher(item.id)}
            className={cn(
              variant === 'underline'
                ? '-mb-px border-b-2 px-3 py-2 text-sm'
                : 'rounded-md px-3 py-1.5 text-sm',
              selecionada
                ? variant === 'underline'
                  ? 'border-(--color-brand) font-medium text-(--color-content)'
                  : 'bg-(--color-surface-muted) font-medium text-(--color-content)'
                : variant === 'underline'
                  ? 'border-transparent text-(--color-content-muted) hover:text-(--color-content)'
                  : 'text-(--color-content-muted) hover:bg-(--color-surface-muted)',
            )}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * O conteúdo da aba da vez.
 *
 * Um só, o da aba selecionada: é o que a `aria-controls` dela aponta, e renderizar os
 * outros escondidos custaria montar telas que ninguém pediu.
 *
 * Sem `tabIndex`: o painel focalizável só é recomendado quando não há nada focalizável
 * dentro dele, e aqui há — campos, botões e tabelas. Pô-lo na ordem de tabulação só
 * acrescentaria uma parada muda antes do primeiro controle de verdade.
 */
export function TabPanel({
  base,
  id,
  className,
  children,
}: {
  readonly base: string;
  readonly id: string;
  readonly className?: string;
  readonly children: ReactNode;
}) {
  return (
    <div
      role="tabpanel"
      id={panelDomId(base, id)}
      aria-labelledby={tabDomId(base, id)}
      className={className}
    >
      {children}
    </div>
  );
}
